/**
 * useRowDrag (A40) — long-press-драг БЛОКОВ строк таблицы, порт эталонных
 * bindDrag/startDrag/blockRows (design-references/screen-18-prices.js:70-110).
 * FLIP на transform: тащимому блоку translateY + scale(1.02) без transition,
 * соседним блокам shift с transition 200ms cubic-bezier(.2,.7,.3,1);
 * гистерезис середины через while (центр тащимого против середины
 * prev/next с учётом их shift); rAF-троттлинг pointermove; отпускание —
 * доводка 200мс (finalOff из shifts) + settleClass, через 210мс чистка
 * стилей и onReorder(ids) в порядке блоков.
 *
 * Взвод: удержание 350мс на драг-ячейке; движение >8px (hypot) до
 * срабатывания отменяет long-press. Требования к разметке хоста:
 *  - драг-ячейка несёт data-si (арбитраж A43: usePullToRefresh не взводит
 *    протяжку на [data-si]) и touch-action:none в CSS (эталон H:322 —
 *    иначе тач уйдёт в нативный скролл до pointermove);
 *  - подстроки блока (спецверсии) идут в DOM сразу за main-строкой —
 *    blockOf собирает их, блок ездит целиком (эталон blockRows);
 *  - dragClass/settleClass — классы css-модуля хоста (эталонные
 *    .dragrow/.settling: outline purple + тень / подавление hover).
 */
import { useRef, type PointerEvent as ReactPointerEvent } from "react";

const LP_MS = 350;
const LP_MOVE = 8;
const EASE = "cubic-bezier(.2,.7,.3,1)";
const SETTLE_MS = 210;

export interface UseRowDragOpts {
  /** Главная строка блока от драг-ячейки (обычно cell.closest("tr[data-…]")). */
  mainOf: (cell: HTMLElement) => HTMLElement | null;
  /** Блок: main + её подстроки в DOM-порядке (эталонный blockRows). */
  blockOf: (main: HTMLElement) => HTMLElement[];
  /** Main-строки того же кластера в DOM-порядке, ВКЛЮЧАЯ саму (эталон cfg.mains). */
  mains: (main: HTMLElement) => HTMLElement[];
  /** id блока из главной строки; undefined — строка вне порядка. */
  idOf: (main: HTMLElement) => string | undefined;
  /** Новый порядок id после доводки (эталон cfg.done). Зовётся всегда. */
  onReorder: (ids: string[]) => void;
  /** Класс тащимых строк на время драга (эталон .dragrow). */
  dragClass?: string;
  /** Класс строк на доводке (эталон .settling). */
  settleClass?: string;
}

export interface RowDrag {
  /** Повесить на onPointerDown драг-ячейки. */
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  /** true от взвода до конца доводки — гасить клики/double-tap хоста. */
  dragged: () => boolean;
}

interface Blk {
  rows: HTMLElement[];
  top: number;
  h: number;
  shift: number;
}

export function useRowDrag(opts: UseRowDragOpts): RowDrag {
  const cfgRef = useRef(opts);
  cfgRef.current = opts;
  const busyRef = useRef(false);
  const draggedRef = useRef(false);

  const startDrag = (tr: HTMLElement, startY: number) => {
    const cfg = cfgRef.current;
    busyRef.current = true;
    draggedRef.current = true;
    const rows = cfg.blockOf(tr);
    const dragH = rows.reduce((a, r) => a + r.getBoundingClientRect().height, 0);
    const homeTop = tr.getBoundingClientRect().top;
    const blocks: Blk[] = [];
    let idx = 0;
    for (const m of cfg.mains(tr)) {
      if (m === tr) {
        idx = blocks.length;
        continue;
      }
      const rs = cfg.blockOf(m);
      blocks.push({
        rows: rs,
        top: m.getBoundingClientRect().top,
        h: rs.reduce((a, r) => a + r.getBoundingClientRect().height, 0),
        shift: 0,
      });
    }
    if (cfg.dragClass) for (const r of rows) r.classList.add(cfg.dragClass);
    for (const bl of blocks) for (const r of bl.rows) r.style.transition = `transform 200ms ${EASE}`;
    let lastY = startY;
    let raf = 0;
    const apply = () => {
      raf = 0;
      const off = lastY - startY;
      for (const r of rows) r.style.transform = `translateY(${off}px) scale(1.02)`;
      /* Гистерезис: центр тащимого против середин prev/next (с их shift). */
      let moved = true;
      while (moved) {
        moved = false;
        const dc = homeTop + off + dragH / 2;
        const prev = idx > 0 ? blocks[idx - 1] : null;
        const next = idx < blocks.length ? blocks[idx] : null;
        if (prev && dc < prev.top + prev.shift + prev.h / 2) {
          prev.shift += dragH;
          idx--;
          moved = true;
        } else if (next && dc > next.top + next.shift + next.h / 2) {
          next.shift -= dragH;
          idx++;
          moved = true;
        }
      }
      for (const bl of blocks)
        for (const r of bl.rows) r.style.transform = bl.shift ? `translateY(${bl.shift}px)` : "";
    };
    const move = (ev: globalThis.PointerEvent) => {
      ev.preventDefault();
      lastY = ev.clientY;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const up = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      document.removeEventListener("pointercancel", up);
      if (raf) {
        cancelAnimationFrame(raf);
        apply();
      }
      const finalOff = blocks.reduce((a, bl) => a + (bl.shift > 0 ? -bl.h : bl.shift < 0 ? bl.h : 0), 0);
      for (const r of rows) {
        r.style.transition = `transform 200ms ${EASE}`;
        r.style.transform = `translateY(${finalOff}px)`;
        if (cfg.settleClass) r.classList.add(cfg.settleClass);
      }
      window.setTimeout(() => {
        for (const r of rows) {
          if (cfg.dragClass) r.classList.remove(cfg.dragClass);
          if (cfg.settleClass) r.classList.remove(cfg.settleClass);
          r.style.transition = "";
          r.style.transform = "";
        }
        for (const bl of blocks)
          for (const r of bl.rows) {
            r.style.transition = "";
            r.style.transform = "";
          }
        const ids: string[] = [];
        const pushId = (m: HTMLElement | undefined) => {
          const id = m ? cfgRef.current.idOf(m) : undefined;
          if (id !== undefined) ids.push(id);
        };
        for (const bl of blocks.slice(0, idx)) pushId(bl.rows[0]);
        pushId(tr);
        for (const bl of blocks.slice(idx)) pushId(bl.rows[0]);
        busyRef.current = false;
        window.setTimeout(() => {
          draggedRef.current = false;
        }, 0);
        cfgRef.current.onReorder(ids);
      }, SETTLE_MS);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    document.addEventListener("pointercancel", up);
    apply();
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button || busyRef.current) return;
    const main = cfgRef.current.mainOf(e.currentTarget);
    if (!main) return;
    const el = e.currentTarget;
    /* Мышь: capture гарантирует pointerup на el и при отпускании вне ячейки —
       иначе LP-таймер не гасился бы и драг стартовал с ненажатой кнопкой
       («фантомный» драг до следующего клика). Тач и так капчурит неявно. */
    try { el.setPointerCapture(e.pointerId); } catch { /* pointer уже неактивен */ }
    const sx = e.clientX;
    const sy = e.clientY;
    let t = 0;
    const cancel = () => {
      window.clearTimeout(t);
      el.removeEventListener("pointerup", cancel);
      el.removeEventListener("pointercancel", cancel);
      el.removeEventListener("pointermove", mv);
    };
    const mv = (ev: globalThis.PointerEvent) => {
      if (Math.hypot(ev.clientX - sx, ev.clientY - sy) > LP_MOVE) cancel();
    };
    t = window.setTimeout(() => {
      cancel();
      startDrag(main, sy);
    }, LP_MS);
    el.addEventListener("pointerup", cancel);
    el.addEventListener("pointercancel", cancel);
    el.addEventListener("pointermove", mv);
  };

  return { onPointerDown, dragged: () => draggedRef.current };
}
